const svgTextCache = new Map<string, Promise<string | null>>();

function loadSvgText(src: string): Promise<string | null> {
  const cached = svgTextCache.get(src);
  if (cached) return cached;

  const request = fetch(src)
    .then((resp) => (resp.ok ? resp.text() : null))
    .catch(() => null);

  svgTextCache.set(src, request);
  return request;
}

async function hydrateSvgElement(el: SVGSVGElement): Promise<void> {
  if (el.dataset['svgHydrated'] === '1') return;

  const src = el.getAttribute('data-svg-src');
  if (!src) return;

  el.dataset['svgHydrated'] = '1';

  const svgText = await loadSvgText(src);
  if (!svgText) {
    el.dataset['svgHydrated'] = 'error';
    return;
  }

  const parsed = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const loadedSvg = parsed.querySelector('svg');
  if (!loadedSvg) {
    el.dataset['svgHydrated'] = 'error';
    return;
  }

  const loadedViewBox = loadedSvg.getAttribute('viewBox');
  if (loadedViewBox && !el.hasAttribute('viewBox')) {
    el.setAttribute('viewBox', loadedViewBox);
  }

  if (!el.hasAttribute('xmlns')) {
    el.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }

  el.innerHTML = loadedSvg.innerHTML;
}

async function hydrateAll(root: ParentNode = document): Promise<void> {
  const svgs = Array.from(root.querySelectorAll('svg[data-svg-src]')) as SVGSVGElement[];
  await Promise.all(svgs.map((el) => hydrateSvgElement(el)));
}

export function setupExternalSvgHydrator(): void {
  void hydrateAll(document);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (!(node instanceof Element)) continue;

        if (node.matches('svg[data-svg-src]')) {
          void hydrateSvgElement(node as SVGSVGElement);
        }

        const nested = Array.from(node.querySelectorAll('svg[data-svg-src]')) as SVGSVGElement[];
        for (const svg of nested) {
          void hydrateSvgElement(svg);
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
